import { Box, Stack } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import type { StateAction } from "@entrilia/shared/types";
import { distinctBy } from "@entrilia/shared/utilities/arrayHelper";
import { api } from "../../../../api/client";
import type { Fund } from "../../../../api/types/commonApiTypes";
import type { FundInvestorContact, InvestorPortalRoleWithNoneValue } from "../../../../api/types/investorContactsTypes";
import type { MembersChangeRequestView } from "../../../../api/types/membersChangeRequestTypes";
import { workspaceInvestorSelector } from "../../../../store/state/user/selectors";
import ConfirmDiscardChangesDialog from "./ConfirmDiscardChangesDialog";
import { getUnsavedChangesLabel } from "./contactChangeLabels";
import type { ContactChangesState } from "./contactChangesState";
import {
  applyContactFundUpdateAction,
  applyContactUpdatesToRows,
  cancelRevertChangeRequestChangeAction,
  changeRequestToContactChanges,
  discardChangesAction,
  getContactUpdateHighlights,
  hasUnsavedChanges,
  mergeContactChanges,
  newContactsToContactRows,
  reconcileRevertedChangeItemIdsAction,
  removeContactAction,
  restoreContactAction,
  revertChangeRequestChangeAction,
} from "./contactChangesState";
import { getStoredContactChanges, storeContactChanges } from "./contactChangesStorage";
import ContactsFilters from "./ContactsFilters";
import ContactsGrid from "./ContactsGrid";
import {
  filterContactRows,
  getContactsById,
  getInitialContactsFilterState,
  type ContactFilterOption,
  type ContactsFilterState,
} from "./contactsListHelper";
import EditContactDrawer from "./EditContactDrawer";
import MembersChangeRequestStatus from "./MembersChangeRequestStatus";
import ReviewChangesDialog from "./ReviewChangesDialog";
import UnsavedChangesAlert from "./UnsavedChangesAlert";

interface Props {
  canUserEdit: boolean;
}

const ContactsList = ({ canUserEdit }: Props) => {
  const workspaceInvestor = useSelector(workspaceInvestorSelector);
  const investorId = workspaceInvestor?.investorId ?? "";

  const [filters, setFilters] = useState<ContactsFilterState>(getInitialContactsFilterState);
  const [contactChanges, setContactChanges] = useState(() => getStoredContactChanges(investorId));
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false);
  const [editedRowId, setEditedRowId] = useState<string>();

  const updateContactChanges = useCallback(
    (action: StateAction<ContactChangesState>) =>
      setContactChanges((previous) => {
        const next = action(previous);
        storeContactChanges(investorId, next);
        return next;
      }),
    [investorId]
  );

  const getInvestorContacts = useCallback(() => api.investorContacts.getInvestorContacts(investorId), [investorId]);

  const [contactsResponse, fetchContactsError, { isFetching }] = useFetch(getInvestorContacts, {
    disabled: !investorId,
  });

  const isChangeRequestEnabled = !!investorId && canUserEdit;

  const getMembersChangeRequest = useCallback(() => api.membersChangeRequests.getStatusView(investorId), [investorId]);

  // Reconciled here (not only on withdrawal) so it also covers another admin withdrawing, an apply/cancel, or a fresh load
  const handleChangeRequestLoaded = useCallback(
    (request: MembersChangeRequestView | undefined) =>
      updateContactChanges(reconcileRevertedChangeItemIdsAction(request)),
    [updateContactChanges]
  );

  const [
    changeRequest,
    fetchChangeRequestError,
    { isFetching: isFetchingChangeRequest, setData: setChangeRequest, fetch: fetchChangeRequest },
  ] = useFetch(getMembersChangeRequest, {
    disabled: !isChangeRequestEnabled,
    onSetData: handleChangeRequestLoaded,
  });

  // Refetch rather than patch local state, so units the server deleted on withdrawal don't leave stale unit tokens
  const handleChangeRequestCanceled = useCallback(() => fetchChangeRequest(), [fetchChangeRequest]);

  const handleFiltersChange = useCallback(
    (patch: Partial<ContactsFilterState>) => setFilters((prev) => ({ ...prev, ...patch })),
    []
  );

  const handleEditContact = useCallback((row: FundInvestorContact) => setEditedRowId(row.id), []);

  const fundInvestors = useMemo(() => workspaceInvestor?.fundInvestors ?? [], [workspaceInvestor]);

  const { newContacts, removedContactIds, removedContactFunds, contactUpdates, revertedChangeItemIds } = contactChanges;

  const changeRequestChanges = useMemo(
    () => changeRequestToContactChanges(changeRequest, fundInvestors, revertedChangeItemIds),
    [changeRequest, fundInvestors, revertedChangeItemIds]
  );

  const displayChanges = useMemo(
    () =>
      mergeContactChanges(changeRequestChanges, {
        newContacts,
        removedContactIds,
        removedContactFunds,
        contactUpdates,
        revertedChangeItemIds,
      }),
    [changeRequestChanges, newContacts, removedContactIds, removedContactFunds, contactUpdates, revertedChangeItemIds]
  );

  // Real change items the request adds, so a revert enqueues a genuine change item id rather than a synthesized grid key
  const changeRequestAddedItemIds = useMemo(() => {
    const itemIds = new Set<string>();
    if (changeRequest?.changeRequestId) {
      changeRequest.items.forEach((item) => {
        if (item.action === "CreateNew" && item.changeItemId) {
          itemIds.add(item.changeItemId);
        }
      });
    }
    return itemIds;
  }, [changeRequest]);

  // Maps a contact removed by the pending change request to the change item that requests it, so it can be reverted
  const changeRequestRemovalItemIdByContactId = useMemo(() => {
    const itemIdByContactId = new Map<string, string>();
    if (changeRequest?.changeRequestId) {
      changeRequest.items.forEach((item) => {
        if (item.action === "Delete" && item.contactId && item.changeItemId) {
          itemIdByContactId.set(item.contactId, item.changeItemId);
        }
      });
    }
    return itemIdByContactId;
  }, [changeRequest]);

  const handleRemoveContact = useCallback(
    (contactId: string) => {
      if (changeRequestAddedItemIds.has(contactId)) {
        updateContactChanges(revertChangeRequestChangeAction(contactId));
        return;
      }

      // Removing a contact whose change-request removal was restored simply reinstates that removal
      const restoredRemovalItemId = changeRequestRemovalItemIdByContactId.get(contactId);
      if (restoredRemovalItemId && revertedChangeItemIds.includes(restoredRemovalItemId)) {
        updateContactChanges(cancelRevertChangeRequestChangeAction(restoredRemovalItemId));
        return;
      }

      updateContactChanges(removeContactAction(contactId));
    },
    [changeRequestAddedItemIds, changeRequestRemovalItemIdByContactId, revertedChangeItemIds, updateContactChanges]
  );

  const handleRestoreContact = useCallback(
    (contactId: string) => {
      const removalItemId = changeRequestRemovalItemIdByContactId.get(contactId);
      updateContactChanges(
        removalItemId ? revertChangeRequestChangeAction(removalItemId) : restoreContactAction(contactId)
      );
    },
    [changeRequestRemovalItemIdByContactId, updateContactChanges]
  );

  // Placeholder rows only exist to be edited, so a read-only viewer sees memberships alone
  const visibleContacts = useMemo(
    () => (contactsResponse?.contacts ?? []).filter((row) => canUserEdit || row.isMember),
    [canUserEdit, contactsResponse]
  );

  const rows = useMemo(
    () => [
      ...applyContactUpdatesToRows(visibleContacts, displayChanges.contactUpdates),
      ...newContactsToContactRows(displayChanges.newContacts),
    ],
    [visibleContacts, displayChanges.contactUpdates, displayChanges.newContacts]
  );

  const updateHighlights = useMemo(
    () => getContactUpdateHighlights(visibleContacts, displayChanges.contactUpdates),
    [visibleContacts, displayChanges.contactUpdates]
  );

  const contactsById = useMemo(() => getContactsById(rows), [rows]);

  const fundOptions = useMemo<Fund[]>(
    () => distinctBy(rows, (row) => row.fundId).map((row) => ({ id: row.fundId, name: row.fundName })),
    [rows]
  );

  const contactOptions = useMemo<ContactFilterOption[]>(
    () =>
      distinctBy(rows, (row) => row.contactId).map((row) => ({
        contactId: row.contactId,
        name: row.contactName,
        email: row.contactEmail,
      })),
    [rows]
  );

  const filteredRows = useMemo(() => filterContactRows(rows, filters), [rows, filters]);

  if (fetchContactsError || fetchChangeRequestError) {
    logError(fetchContactsError || fetchChangeRequestError, "[ContactsList]");
    return <DataLoadingFailed title="Failed to load contacts" />;
  }

  if (
    contactsResponse === undefined ||
    isFetching ||
    isFetchingChangeRequest ||
    (isChangeRequestEnabled && changeRequest === undefined)
  ) {
    return <InlineLoader />;
  }

  const handleDiscardChangesConfirmed = () => {
    updateContactChanges(discardChangesAction());
    setShowDiscardConfirmation(false);
    setShowReviewDialog(false);
  };

  const handleChangeRequestSubmitted = (updatedChangeRequest: MembersChangeRequestView) => {
    setChangeRequest(updatedChangeRequest);
    updateContactChanges(discardChangesAction());
    setShowReviewDialog(false);
  };

  // Edits amend the pending change request, so the drawer opens on the change-request state, not the stored contact
  const editedStoredRow = editedRowId ? contactsResponse.contacts.find((row) => row.id === editedRowId) : undefined;
  const editedRow = editedStoredRow
    ? applyContactUpdatesToRows([editedStoredRow], changeRequestChanges.contactUpdates)[0]
    : undefined;
  const editedRowPendingUpdate =
    editedRow &&
    contactChanges.contactUpdates.find(
      (update) => update.contactId === editedRow.contactId && update.fundId === editedRow.fundId
    );

  const handleApplyContactUpdate = (role: InvestorPortalRoleWithNoneValue, categoryExternalIds: string[]) => {
    if (!editedRow) {
      return;
    }

    updateContactChanges(
      applyContactFundUpdateAction(
        { contactId: editedRow.contactId, fundId: editedRow.fundId, role, categoryExternalIds },
        editedRow
      )
    );
    setEditedRowId(undefined);
  };

  return (
    <Stack spacing={2} sx={{ height: "100%", minHeight: 0 }}>
      {changeRequest && (
        <MembersChangeRequestStatus
          canUserEdit={canUserEdit}
          investorId={investorId}
          changeRequest={changeRequest}
          onChangeRequestCanceled={handleChangeRequestCanceled}
        />
      )}
      {hasUnsavedChanges(contactChanges) && (
        <UnsavedChangesAlert
          changesLabel={getUnsavedChangesLabel(contactChanges)}
          hasPendingChangeRequest={!!changeRequest?.changeRequestId}
          onDiscardChanges={() => setShowDiscardConfirmation(true)}
          onReviewAndSubmit={() => setShowReviewDialog(true)}
        />
      )}
      <ContactsFilters
        fundOptions={fundOptions}
        contactOptions={contactOptions}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        recordsCount={filteredRows.length}
        totalRecordsCount={rows.length}
      />
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ContactsGrid
          rows={filteredRows}
          contactsById={contactsById}
          contactChanges={displayChanges}
          updateHighlights={updateHighlights}
          isEditable={canUserEdit}
          onRemoveContact={handleRemoveContact}
          onRestoreContact={handleRestoreContact}
          onEditContact={handleEditContact}
        />
      </Box>
      {editedRow && (
        <EditContactDrawer
          contact={editedRow}
          pendingUpdate={editedRowPendingUpdate}
          onApply={handleApplyContactUpdate}
          onClose={() => setEditedRowId(undefined)}
        />
      )}
      {showReviewDialog && (
        <ReviewChangesDialog
          onClose={() => setShowReviewDialog(false)}
          investorId={investorId}
          changesState={contactChanges}
          setChangesState={updateContactChanges}
          contacts={contactsResponse.contacts}
          fundInvestors={fundInvestors}
          latestChangeRequest={changeRequest}
          onDiscardChanges={() => setShowDiscardConfirmation(true)}
          onSubmitted={handleChangeRequestSubmitted}
        />
      )}
      <ConfirmDiscardChangesDialog
        open={showDiscardConfirmation}
        onClose={() => setShowDiscardConfirmation(false)}
        onConfirm={handleDiscardChangesConfirmed}
      />
    </Stack>
  );
};

export default ContactsList;
